import type {PullRequestMergeRequirementsPayload, StackEntryPayload} from '../../../page-data/payloads/merge-box'
import type {StatusChecksPageData} from '../../../page-data/payloads/status-checks'
import {hasFailingRequiredChecks, hasPendingRequiredChecks} from '../../mergeability-status'
import {EffectiveEntryStatus} from '../../effective-entry-status'
import {USER_CANNOT_PUSH_SUBCONDITION} from '../../json-api-helpers'
import type {StackSectionStackEntry} from '../stack-section-types'
import {hydrateMergeBoxStackEntriesWithStackMergeabilityData} from './hydrate-merge-box-stack-entries-with-stack-mergeability-data'
import type {StackMergeabilityPageData} from '../../../page-data/payloads/stack-mergeability'
import type {UseQueryResult} from '@github-ui/react-query'

export type StackEntriesContext = {
  mergeRequirements: PullRequestMergeRequirementsPayload | null
  isRebasing: boolean
  queries: Array<UseQueryResult<StackMergeabilityPageData>>
  // Status checks for the CURRENT pull request being viewed.
  statusChecks?: StatusChecksPageData
}

// Statuses that merging propagation leaves untouched.
const MERGING_IGNORED_STATUSES = new Set<EffectiveEntryStatus>([
  EffectiveEntryStatus.Closed,
  EffectiveEntryStatus.Merged,
  EffectiveEntryStatus.Merging,
  EffectiveEntryStatus.Queued,
])

// Statuses that are static and shouldn't be changed
const STATIC_STATUSES = new Set<EffectiveEntryStatus>([
  EffectiveEntryStatus.Closed,
  EffectiveEntryStatus.Draft,
  EffectiveEntryStatus.MergeConflicts,
  EffectiveEntryStatus.Merged,
  EffectiveEntryStatus.Merging,
  EffectiveEntryStatus.NeedsRebase,
  EffectiveEntryStatus.Queued,
])

function allRequiredChecksPassed(statusChecks: StatusChecksPageData | undefined): boolean {
  return !hasFailingRequiredChecks(statusChecks) && !hasPendingRequiredChecks(statusChecks)
}

function hasOkServerStatus(entry: StackSectionStackEntry): boolean {
  return 'status' in entry && entry.status === 'OK'
}

function hasOnlyViewerPushPermissionFailure(mergeRequirements: PullRequestMergeRequirementsPayload | null): boolean {
  if (!mergeRequirements) return false

  const failedConditions = mergeRequirements.conditions.filter(condition => condition.result === 'FAILED')
  if (failedConditions.length !== 1) return false

  const [failedCondition] = failedConditions
  if (!failedCondition) return false

  if (failedCondition.type !== 'PULL_REQUEST_USER_STATE') return false

  if (failedCondition.failedSubConditions.length < 1) return false

  if (
    failedCondition.failedSubConditions.every(
      subCondition => subCondition.displayName === USER_CANNOT_PUSH_SUBCONDITION,
    )
  )
    return true

  return false
}

function hasFailedNotStackConditions(mergeRequirements: PullRequestMergeRequirementsPayload | null): boolean {
  if (!mergeRequirements) return false
  // Exclude the STACK rollup condition: a downstack NEEDS_REBASE/MERGE_CONFLICTS
  // failure fails the STACK condition on the CURRENT pull request, but it is typically not a
  // failure of the CURRENT entry itself. Treating it as one would always make this entry NotReady
  // for the CURRENT entry, preventing the reverse pass from
  // surfacing it as 'BLOCKED_DOWNSTACK' when the stack entry was actually in 'READY' state.
  // Other Rule failures for NEEDS_REBASE/MERGE_CONFLICTS would appear in the other conditions,
  // so the current entry would be handled correctly.
  return mergeRequirements.conditions.some(condition => condition.type !== 'STACK' && condition.result === 'FAILED')
}

// Returns a refined effectiveEntryStatus for the CURRENT stack entry
function refineCurrentEntryStatus(
  entry: StackSectionStackEntry,
  {mergeRequirements, statusChecks}: StackEntriesContext,
): EffectiveEntryStatus | undefined {
  // Never override a terminal/static status: a closed, merge-conflicts, needs rebase, draft, queued, merging,
  // or merged stack entry effective status should stay as-is
  // regardless of the stack's merge requirements state.
  if (entry.effectiveEntryStatus && STATIC_STATUSES.has(entry.effectiveEntryStatus)) {
    return entry.effectiveEntryStatus
  }

  const state = mergeRequirements?.state

  if (mergeRequirements === null || state === 'UNKNOWN') return EffectiveEntryStatus.Loading

  if (state === 'UNMERGEABLE') {
    if (hasOnlyViewerPushPermissionFailure(mergeRequirements)) {
      return EffectiveEntryStatus.Ready
    }

    const hasFailedSubConditions = (entry.failedSubConditions?.length ?? 0) > 0
    if (hasFailedSubConditions) {
      return EffectiveEntryStatus.NotReady
    }

    if (hasFailedNotStackConditions(mergeRequirements)) {
      return EffectiveEntryStatus.NotReady
    }

    if (entry.effectiveEntryStatus === EffectiveEntryStatus.Ready) {
      return entry.effectiveEntryStatus
    }

    // In production, CURRENT entries with server status OK often have no
    // stack_mergeability query data yet and hydrate as Loading. For stack-driven
    // UNMERGEABLE states we still need CURRENT to be Ready here so reverse-pass
    // blocking can surface it as BlockedDownstack.
    if (hasOkServerStatus(entry)) {
      return EffectiveEntryStatus.Ready
    }

    return EffectiveEntryStatus.NotReady
  }

  if (state === 'MERGEABLE_IF_STATUSES_PASS') {
    // A MERGEABLE_IF_STATUSES_PASS mergeability state is only `Ready` once its required checks
    // pass. Treat missing status-check data as `NotReady`. As the data hasn't loaded,
    // we can't confirm the checks pass.
    if (statusChecks === undefined) {
      return EffectiveEntryStatus.NotReady
    }

    return allRequiredChecksPassed(statusChecks) ? EffectiveEntryStatus.Ready : EffectiveEntryStatus.NotReady
  }

  // A `MERGEABLE` mergeability state on the current entry should always return `Ready`
  // at this point in the refining stage.
  if (state === 'MERGEABLE') {
    return EffectiveEntryStatus.Ready
  }

  return entry.effectiveEntryStatus
}

/**
 * Builds the list of stack entries with their effective statuses in three passes.
 *
 * 1. Hydrate pass: turn each StackEntryPayload into a StackSectionStackEntry.
 * 2. Forward pass: refine the CURRENT entry's status and note if any entry is Merging.
 * 3. Reverse pass: apply merging propagation, not-ready propagation, and the
 *    rebase overlay together.
 *
 * The reverse pass can apply the `BlockedDownstack`, `Merging` or `Loading` effective entry statuses at once because:
 *   - `Merging` status propagation for an entry only needs that entry's position and
 *     status plus the shared `topmostMergingIndex` variable set.
 *   - `BlockedDownstack` propagation is the only step that cares about order, and it reads
 *     the status after merging, so doing merging first ensures order correctness.
 *   - The rebase overlay just remaps one entry at a time, so it runs last without
 *     changing `priorBlocked`.
 *
 * ```text
 *   ┌─────────────────────────┐   ┌──────────────────────────────┐
 *   │  entries                │   │  context.queries             │
 *   │  (merge_box API)        │   │  (stack_mergeability API)    │
 *   └────────────┬────────────┘   └───────────────┬──────────────┘
 *                │                                │
 *                └─ index (entry[i] ↔ query[i]) ──┘
 *                               │
 *                               ▼
 *   ╔════════════════════════════════════════════════════════════╗
 *   ║ PASS 1 — HYDRATE                                           ║
 *   ║   StackEntryPayload ──► StackSectionStackEntry             ║
 *   ║   merge .channels + .statusCheckChannels ► AliveChannels   ║
 *   ║   seed effectiveEntryStatus                                ║
 *   ║     (static wins, else query, else placeholder)            ║
 *   ╚════════════════════════════════════════════════════════════╝
 *                               │ hydratedStackEntries
 *                               ▼
 *   ╔════════════════════════════════════════════════════════════╗
 *   ║ PASS 2 — FORWARD  (i = 0 ─► n-1)                           ║
 *   ║   CURRENT entry ► refineCurrentEntryStatus                 ║
 *   ║     (mergeRequirements, statusChecks)                      ║
 *   ║   track topmostMergingIndex                                ║
 *   ╚════════════════════════════════════════════════════════════╝
 *                               │ refinedStackEntries + topmostMergingIndex
 *                               ▼
 *   ╔════════════════════════════════════════════════════════════╗
 *   ║ PASS 3 — REVERSE  (i = n-1 ─► 0, priorBlocked = false)     ║
 *   ║   1. propagateMerging:                                     ║
 *   ║       i > topmostMergingIndex & !ignored ► Merging         ║
 *   ║   2. propagateBlockedDownstack:                            ║
 *   ║       Ready + priorBlocked ► BlockedDownstack              ║
 *   ║       NotReady | Draft | Closed |                          ║
 *   ║.      MergeConflicts | NeedsRebase ► priorBlocked = true   ║
 *   ║   3. overlayRebaseState:                                   ║
 *   ║       isRebasing &                                         ║
 *   ║       (Ready|NotReady|BlockedDownstack                     ║
 *   ║.       MergeConflicts | NeedsRebase) ► Loading             ║
 *   ╚════════════════════════════════════════════════════════════╝
 *                               │
 *                               ▼
 *            currentStackEntries: StackSectionStackEntry[]
 *
 *   Stack layout (index 0 = topmost/upstack ─► last index = base/downstack);
 *   the reverse pass walks base ─► top:
 *
 *     index   position
 *       0      AFTER    ┐ top / upstack
 *       k     CURRENT   │ refined in Pass 2
 *       …     BEFORE    │ Merging propagates DOWNSTACK from the topmost merge…
 *      n-1    BEFORE    ┘ base; NotReady/Draft/Closed/MergeConflicts/NeedsRebase blocks UPWARD via priorBlocked
 * ```
 */
export function buildCurrentStackEntries(
  entries: StackEntryPayload[],
  context: StackEntriesContext,
): StackSectionStackEntry[] {
  // Hydrate stack entries by combining stack entries from the
  // `merge_box` API data and `stack_mergeability` API data (when it exists).
  // This is our initial source of truth before setting dynamic effectiveEntryStatuses
  // that are contextually aware of sibling StackEntries.
  const hydratedStackEntries = hydrateMergeBoxStackEntriesWithStackMergeabilityData(context.queries)(entries)

  // Forward pass: refine the CURRENT entry and find the topmost (most upstack,
  // i.e. lowest-index) entry that is Merging. A stack merges from the base up, so
  // every entry downstack of that topmost merging entry is part of the same
  // in-progress merge. Entries are ordered top (index 0) ─► base (last index).
  let topmostMergingIndex: number | undefined
  const refinedStackEntries: StackSectionStackEntry[] = new Array(hydratedStackEntries.length)
  for (let i = 0; i < hydratedStackEntries.length; i++) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const entry = hydratedStackEntries[i]!
    let updatedEntry = entry

    if (entry.position === 'CURRENT') {
      const status = refineCurrentEntryStatus(entry, context)
      if (status !== entry.effectiveEntryStatus) updatedEntry = {...entry, effectiveEntryStatus: status}
    }

    if (topmostMergingIndex === undefined && updatedEntry.effectiveEntryStatus === EffectiveEntryStatus.Merging) {
      topmostMergingIndex = i
    }
    refinedStackEntries[i] = updatedEntry
  }

  // Reverse pass: merging propagation + not-ready propagation + rebase overlay.
  const currentStackEntries: StackSectionStackEntry[] = new Array(refinedStackEntries.length)
  let priorBlocked = false
  for (let i = refinedStackEntries.length - 1; i >= 0; i--) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const entry = refinedStackEntries[i]!
    let status = entry.effectiveEntryStatus

    // Step 1: propagateMerging - Every entry downstack of the topmost Merging
    // entry (higher index) is part of the same bottom-up stack merge, so it also
    // becomes Merging unless it is in a terminal/ignored state. Entries upstack of
    // the topmost merging entry keep their own status.
    if (
      topmostMergingIndex !== undefined &&
      i > topmostMergingIndex &&
      !(status && MERGING_IGNORED_STATUSES.has(status))
    ) {
      status = EffectiveEntryStatus.Merging
    }

    // Step 2: propagateBlockedDownstack - A Ready entry that sits above a downstack
    // NotReady, MergeConflicts, NeedsRebase, Draft, or Closed entry can't merge yet, so it surfaces as
    // BlockedDownstack (it would be Ready on its own). A truly NotReady entry
    // stays NotReady, while Draft and Closed entries keep their own status and
    // still block everything stacked above them.
    if (priorBlocked && status === EffectiveEntryStatus.Ready) {
      status = EffectiveEntryStatus.BlockedDownstack
    }

    if (
      status === EffectiveEntryStatus.Draft ||
      status === EffectiveEntryStatus.Closed ||
      status === EffectiveEntryStatus.MergeConflicts ||
      status === EffectiveEntryStatus.NeedsRebase ||
      status === EffectiveEntryStatus.NotReady
    ) {
      priorBlocked = true
    }

    // Step 3: overlayRebaseState - While rebasing, Ready/NotReady/BlockedDownstack/MergeConflicts/NeedsRebase become Loading.
    if (
      context.isRebasing &&
      (status === EffectiveEntryStatus.Ready ||
        status === EffectiveEntryStatus.NotReady ||
        status === EffectiveEntryStatus.BlockedDownstack ||
        status === EffectiveEntryStatus.MergeConflicts ||
        status === EffectiveEntryStatus.NeedsRebase)
    ) {
      status = EffectiveEntryStatus.Loading
    }

    currentStackEntries[i] = status === entry.effectiveEntryStatus ? entry : {...entry, effectiveEntryStatus: status}
  }

  return currentStackEntries
}
